import * as React from "react";
import { useParams } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  Coins,
  Building2,
} from "lucide-react";
import {
  AreaChart,
  Area,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { cn } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "../../components/ui-v2/card";
import { Button } from "../../components/ui-v2/button";
import { StatCard } from "../../components/ui-v2/stat-card";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { SegmentedControl, SegmentedControlOption } from "../../components/ui-v2/segmented-control";
import {
  CHART_ACCENT_COLOR,
  chartGradientStops,
  DONUT_SLICE_COLORS,
  donutRingProps,
  getChartAnimationProps,
} from "../../components/ui-v2/chart-theme";
import { ChartTooltipShell, ChartTooltipRow } from "../../components/ui-v2/chart-tooltip";
import {
  CurrencyValue,
  PercentValue,
} from "../../components/ui-v2/value-display";
import { Text, Label, MonoText } from "../../components/ui-v2/typography";
import { usePortfolio } from "../../hooks/usePortfolio";
import { useSnapshots } from "../../hooks/useSnapshots";
import { useAnalyticsSummary, useETFComparison, usePerformanceHeatmap } from "../../hooks/useAnalytics";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import TopPerformers from "../../components/ui-v2/TopPerformers";
import ConcentrationRisk from "../../components/ui-v2/ConcentrationRisk";
import ETFComparison from "../../components/ui-v2/ETFComparison";
import PerformanceHeatmap from "../../components/ui-v2/PerformanceHeatmap";
import WhatIfSimulator from "../../components/ui-v2/WhatIfSimulator";

/**
 * V2 Analytics Page — Institutional Console redesign (issue #237,
 * DESIGN-SPEC.md §5 "Analytics/Performance").
 *
 * Sub-tab routing (Insights / Performance / What-if) is unchanged — driven
 * by the existing `/analytics/:type` route + PageToolbar (§3). This page:
 * - Performance tab: fully rebuilt to §5 "KPI strip → value chart + allocation
 *   donut with best/worst mini-cards → Top movers (diverging bars) + Time-in-
 *   market distribution", matching Analytics.dc.html exactly.
 * - Insights tab: header/toggle/summary chrome retheme to console tokens;
 *   the TopPerformers/ConcentrationRisk/ETFComparison/PerformanceHeatmap
 *   sub-components are NOT touched here — TopPerformers is also consumed by
 *   Dashboard.jsx (owned by the concurrent #235 agent) and the other three
 *   already inherit the restyled Card/Badge/value-display primitives from
 *   #233/#234, so leaving them as-is carries no visual regression risk while
 *   avoiding an out-of-scope shared-file edit. Declared as a scoping
 *   deviation in the session file.
 * - What-if tab: WhatIfSimulator is a large (750-line) self-contained
 *   component not named in the issue's screen bullet ("value chart + donut +
 *   best/worst mini-cards + diverging Top-movers bars + time-in-market" is
 *   the Performance tab only) — left untouched.
 */

// Chart color palette for the Insights-tab allocation cards (Currency/
// Platform breakdowns) — unrelated to the Performance-tab donut, which uses
// DONUT_SLICE_COLORS from chart-theme.js (§4.5 "accent/positive/neutral-1/
// gold" — the ONLY palette permitted there per §7 "never semantic
// green/red" for comparison/allocation series... except allocation slices
// ARE the primary metric here, not a benchmark, so DONUT_SLICE_COLORS
// applies directly, not the BENCHMARK_COLORS carve-out).
const INSIGHTS_ALLOCATION_COLORS = [
  "hsl(221, 83%, 53%)",
  "hsl(142, 71%, 45%)",
  "hsl(45, 93%, 47%)",
  "hsl(262, 83%, 58%)",
  "hsl(38, 92%, 50%)",
];

// Performance-tab chart/KPI range switch (§5 SegmentedControl 1M/3M/1Y/All).
// RANGE_DAYS drives the actual data fetch; RANGE_LABELS/RANGE_PERIOD_NOUNS
// are two grammatical forms of the SAME range→text mapping (adjective
// "90-day change" vs noun-phrase "90 days") kept as sibling const objects
// (not derived string surgery) so both the chart title ("Portfolio value —
// {noun}") and the KPI card label ("{label} change") are driven by `range`
// directly and can never drift out of sync again (round-2 fix, architect
// review Finding 1 — previously the KPI *value* tracked `range` via
// `chartData`/`useSnapshots(RANGE_DAYS[range])`, but its label was a
// hardcoded "90-day change", lying for any range other than 3M; the chart
// title had its own correct-but-duplicated inline range→text expression).
const RANGE_DAYS = { "1M": 30, "3M": 90, "1Y": 365, All: 3650 };
const RANGE_LABELS = { "1M": "30-day", "3M": "90-day", "1Y": "365-day", All: "All-time" };
const RANGE_PERIOD_NOUNS = { "1M": "30 days", "3M": "90 days", "1Y": "365 days", All: "all time" };

// Real estate asset types for filtering
const REAL_ESTATE_TYPES_PERF = ["Real Estate", "Real Estate & Property"];

// Asset types excluded from performance rankings
const PERFORMANCE_EXCLUDED_TYPES = [
  "Cash/Bank Account",
  "Vehicle",
  "Equipment",
  "Other High-Value Asset",
];

// §5 "Time in market" age buckets — mirrors HoldingAgeInsight.jsx's
// AGE_BUCKETS (Dashboard-owned, not imported here per file-ownership rules;
// duplicated locally since it's a small, self-contained bucket definition).
const AGE_BUCKETS = [
  { id: "0-6m", label: "< 6 months", min: 0, max: 182 },
  { id: "6m-1y", label: "6-12 months", min: 182, max: 365 },
  { id: "1-2y", label: "1-2 years", min: 365, max: 730 },
  { id: "2-5y", label: "2-5 years", min: 730, max: 1825 },
  { id: "5y+", label: "5+ years", min: 1825, max: Infinity },
];

function calculateAgeDays(dateAcquired) {
  if (!dateAcquired) return null;
  const acquired = new Date(dateAcquired);
  const now = new Date();
  return Math.floor((now.getTime() - acquired.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Format a signed EUR amount for StatCard values (§7 "gains/losses always
 * carry sign"). Uses a true minus sign (−, U+2212) for negatives to match
 * the mockup's typographic convention (Analytics.dc.html / Snapshots.dc.html
 * both use −, not the ASCII hyphen-minus).
 */
function formatSignedCurrency(value) {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return `${value >= 0 ? "+" : "−"}${formatted}`;
}

function formatAgeShort(days) {
  if (days === null || days === undefined) return "--";
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  if (years === 0) return `${months}m`;
  if (months === 0) return `${years}y`;
  return `${years}y ${months}m`;
}

/**
 * Time-in-market bucket analysis (§5/§4.5 "Time in market distribution").
 */
function analyzeTimeInMarket(holdings) {
  const investments = (holdings || []).filter(
    (h) => !h.isLiability && ["Stock", "ETF", "Cryptocurrency", "Precious Metal"].includes(h.assetType)
  );

  const withAge = investments
    .map((h) => ({ ...h, ageDays: calculateAgeDays(h.dateAcquired) }))
    .filter((h) => h.ageDays !== null);

  if (withAge.length === 0) return null;

  const buckets = AGE_BUCKETS.map((bucket) => {
    const inBucket = withAge.filter((h) => h.ageDays >= bucket.min && h.ageDays < bucket.max);
    const avgGainPercent =
      inBucket.length > 0
        ? inBucket.reduce((sum, h) => sum + (h.gainPercent || 0), 0) / inBucket.length
        : 0;
    return { ...bucket, count: inBucket.length, avgGainPercent };
  });

  const totalDays = withAge.reduce((sum, h) => sum + h.ageDays, 0);
  const avgDays = totalDays / withAge.length;

  const longestHeld = withAge.reduce((longest, h) => (h.ageDays > (longest?.ageDays || 0) ? h : longest));

  const newHoldings = withAge.filter((h) => h.ageDays < 365);
  const oldHoldings = withAge.filter((h) => h.ageDays >= 365);
  const newAvgGain =
    newHoldings.length > 0
      ? newHoldings.reduce((sum, h) => sum + (h.gainPercent || 0), 0) / newHoldings.length
      : null;
  const oldAvgGain =
    oldHoldings.length > 0
      ? oldHoldings.reduce((sum, h) => sum + (h.gainPercent || 0), 0) / oldHoldings.length
      : null;

  return { buckets, avgDays, longestHeld, newAvgGain, oldAvgGain };
}

/**
 * TimeInMarketPanel — §5/Analytics.dc.html "Time in market" card: bucketed
 * bar rows + summary line comparing >1yr vs <1yr average gain.
 */
function TimeInMarketPanel({ holdings, loading }) {
  const analysis = React.useMemo(() => analyzeTimeInMarket(holdings), [holdings]);
  const maxCount = analysis ? Math.max(...analysis.buckets.map((b) => b.count), 1) : 1;

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-hairline px-[18px] py-3">
        <span className="text-[13.5px] font-semibold text-foreground">Time in market</span>
        {analysis && (
          <MonoText size="xs" className="font-normal text-text-faint">
            avg {formatAgeShort(Math.round(analysis.avgDays))}
          </MonoText>
        )}
      </div>
      <CardContent className="flex flex-col gap-2 px-[18px] py-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
          </div>
        ) : !analysis ? (
          <Text variant="secondary" size="sm" className="py-4 text-center">
            No data available
          </Text>
        ) : (
          <>
            {analysis.buckets.map((bucket) => (
              <div key={bucket.id} className="flex items-center gap-2.5">
                <span className="w-[90px] shrink-0 text-[11.5px] text-text-secondary">{bucket.label}</span>
                <div className="h-3.5 flex-1 overflow-hidden rounded-[3px] bg-[rgba(255,255,255,0.04)]">
                  <div
                    className="h-full bg-[var(--accent,_#3b9eff)]"
                    style={{
                      width: `${(bucket.count / maxCount) * 100}%`,
                      opacity: 0.4 + (bucket.count / maxCount) * 0.6,
                    }}
                  />
                </div>
                <span className="w-6 shrink-0 text-right font-mono text-[11.5px] text-text-secondary">
                  {bucket.count}
                </span>
                <span
                  className={cn(
                    "w-16 shrink-0 text-right font-mono text-[11.5px]",
                    bucket.count === 0 ? "text-text-faint" : bucket.avgGainPercent >= 0 ? "text-success" : "text-destructive"
                  )}
                >
                  {bucket.count === 0 ? "--" : `${bucket.avgGainPercent >= 0 ? "+" : ""}${bucket.avgGainPercent.toFixed(1)}%`}
                </span>
              </div>
            ))}
            <div className="mt-1 flex items-center justify-between border-t border-hairline pt-2.5 text-[11.5px]">
              <span className="text-text-secondary">
                Positions held &gt;1yr outperform:{" "}
                {analysis.oldAvgGain !== null && analysis.newAvgGain !== null ? (
                  <>
                    <b className="font-mono font-medium text-success">
                      {analysis.oldAvgGain >= 0 ? "+" : ""}
                      {analysis.oldAvgGain.toFixed(1)}%
                    </b>{" "}
                    vs{" "}
                    <b className="font-mono font-medium text-text-secondary">
                      {analysis.newAvgGain >= 0 ? "+" : ""}
                      {analysis.newAvgGain.toFixed(1)}%
                    </b>
                  </>
                ) : (
                  <b className="font-mono font-medium text-text-faint">--</b>
                )}
              </span>
              {analysis.longestHeld && (
                <span className="text-text-faint">
                  Longest: {analysis.longestHeld.name || analysis.longestHeld.assetName} ·{" "}
                  {formatAgeShort(analysis.longestHeld.ageDays)}
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * TopMoversPanel — §5/Analytics.dc.html "Top movers — 90 days" diverging
 * bars (bar grows left from center for losses, right from center for gains).
 */
function TopMoversPanel({ topPerformers, bottomPerformers, loading }) {
  const movers = React.useMemo(() => {
    const combined = [...(topPerformers || []), ...(bottomPerformers || [])];
    // De-dupe (a holding could theoretically appear in both lists on tiny
    // portfolios) and sort by |gainPercent| descending, matching the
    // mockup's ranked diverging-bar list.
    const seen = new Set();
    const unique = combined.filter((h) => {
      const key = h.holdingId || h.name || h.assetName;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return unique.sort((a, b) => Math.abs(b.gainPercent || 0) - Math.abs(a.gainPercent || 0)).slice(0, 5);
  }, [topPerformers, bottomPerformers]);

  const maxAbs = React.useMemo(
    () => Math.max(...movers.map((m) => Math.abs(m.gainPercent || 0)), 1),
    [movers]
  );

  return (
    <Card>
      <div className="border-b border-hairline px-[18px] py-3">
        <span className="text-[13.5px] font-semibold text-foreground">Top movers — 90 days</span>
      </div>
      <CardContent className="px-[18px] py-1.5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
          </div>
        ) : movers.length === 0 ? (
          <Text variant="secondary" size="sm" className="py-4 text-center">
            No performance data available
          </Text>
        ) : (
          movers.map((m) => {
            const pct = m.gainPercent || 0;
            const isUp = pct >= 0;
            const widthPercent = (Math.abs(pct) / maxAbs) * 50;
            return (
              <div
                key={m.holdingId || m.name || m.assetName}
                className="flex items-center gap-3 border-b border-hairline py-2 last:border-b-0"
              >
                <span className="w-[170px] shrink-0 truncate text-[12.5px] font-medium text-foreground">
                  {m.name || m.assetName}
                </span>
                <span className="w-[76px] shrink-0 font-mono text-[11px] text-text-faint">
                  {m.ticker || ""}
                </span>
                <div className="flex h-3.5 flex-1 items-center">
                  <div
                    className={cn("h-1.5 rounded-[3px]", isUp ? "bg-success" : "bg-destructive")}
                    style={{
                      width: `${widthPercent}%`,
                      marginLeft: isUp ? "50%" : `${50 - widthPercent}%`,
                    }}
                  />
                </div>
                <span
                  className={cn("w-[70px] shrink-0 text-right font-mono text-xs", isUp ? "text-success" : "text-destructive")}
                >
                  {isUp ? "+" : "−"}
                  {Math.abs(pct).toFixed(1)}%
                </span>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

/**
 * PortfolioValueChart — §5/Analytics.dc.html "Portfolio value — 90 days"
 * area+line chart, themed via chart-theme.js per §4.5.
 */
function PortfolioValueChart({ data, loading }) {
  const prefersReducedMotion = usePrefersReducedMotion();

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "2-digit" }).toUpperCase();

  const tickIndexes = React.useMemo(() => {
    const n = data?.length || 0;
    if (n === 0) return [];
    if (n <= 4) return data.map((_, i) => i);
    const step = (n - 1) / 3;
    return [0, 1, 2, 3].map((i) => Math.round(i * step));
  }, [data]);

  if (loading) {
    return (
      <div className="flex h-[230px] w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-[230px] w-full items-center justify-center">
        <Text variant="secondary" size="sm">
          No snapshot data available
        </Text>
      </div>
    );
  }

  return (
    <>
      <div className="h-[230px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id="analyticsPortfolioValueGradient" x1="0" y1="0" x2="0" y2="1">
                {chartGradientStops.map((stop) => (
                  <stop key={stop.offset} offset={stop.offset} stopColor={stop.stopColor} stopOpacity={stop.stopOpacity} />
                ))}
              </linearGradient>
            </defs>
            <RechartsTooltip
              content={({ active, payload }) => {
                const point = payload?.[0]?.payload;
                if (!point) return null;
                return (
                  <ChartTooltipShell active={active} payload={payload} label={formatDate(point.date)}>
                    <ChartTooltipRow
                      label="Portfolio value"
                      value={new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(point.value)}
                      swatchColor={CHART_ACCENT_COLOR}
                    />
                  </ChartTooltipShell>
                );
              }}
            />
            <Area
              type="linear"
              dataKey="value"
              stroke={CHART_ACCENT_COLOR}
              strokeWidth={2}
              fill="url(#analyticsPortfolioValueGradient)"
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
              {...getChartAnimationProps(prefersReducedMotion)}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex justify-between font-mono text-[10px] text-text-faint">
        {tickIndexes.map((index) => (
          <span key={`${data[index].date}-${index}`}>{formatDate(data[index].date)}</span>
        ))}
      </div>
    </>
  );
}

/**
 * AllocationDonut — §5/§4.5 donut ring (24px width, 2° gaps,
 * accent/positive/neutral-1/gold slice order) + legend list.
 */
function AllocationDonut({ data, loading }) {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (loading) {
    return (
      <div className="flex h-[140px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-[140px] items-center justify-center">
        <Text variant="secondary" size="xs">
          No data
        </Text>
      </div>
    );
  }

  const ring = donutRingProps({ outerRadius: 55 });

  return (
    <div className="mt-3.5 flex items-center gap-4">
      <div className="h-[110px] w-[110px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              {...ring}
              {...getChartAnimationProps(prefersReducedMotion)}
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={DONUT_SLICE_COLORS[index % DONUT_SLICE_COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip
              content={({ active, payload }) => {
                const point = payload?.[0]?.payload;
                if (!point) return null;
                return (
                  <ChartTooltipShell active={active} payload={payload} label={point.name}>
                    <ChartTooltipRow
                      label="Share"
                      value={`${point.percentage.toFixed(1)}%`}
                      swatchColor={payload[0].payload.color || DONUT_SLICE_COLORS[0]}
                    />
                  </ChartTooltipShell>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-1 flex-col gap-[7px] text-xs">
        {data.map((item, index) => (
          <div key={item.name} className="flex items-center gap-[7px]">
            <span
              className="h-2 w-2 shrink-0 rounded-sm"
              style={{ backgroundColor: DONUT_SLICE_COLORS[index % DONUT_SLICE_COLORS.length] }}
              aria-hidden="true"
            />
            <span className="flex-1 text-text-secondary">{item.name}</span>
            <span className="font-mono text-foreground">{item.percentage.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Performance Page Component (§5 "Analytics/Performance")
function PerformancePage() {
  const [excludeRealEstate, setExcludeRealEstate] = React.useState(false);
  const [range, setRange] = React.useState("3M");

  const {
    holdings,
    summary,
    loading: portfolioLoading,
    refresh: refreshPortfolio,
  } = usePortfolio();

  const {
    snapshots,
    loading: snapshotsLoading,
    refresh: refreshSnapshots,
  } = useSnapshots(RANGE_DAYS[range]);

  // Human-readable label for the selected range — single source of truth for
  // both the chart title ("Portfolio value — {label}") and the KPI card
  // label ("{label} change"), so they can never drift apart again (round-2
  // fix, architect review Finding 1: the KPI value already tracked `range`
  // via `chartData`, but its label was hardcoded "90-day change", lying for
  // any range other than 3M).
  const rangeChangeLabel = RANGE_LABELS[range];

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refreshPortfolio(), refreshSnapshots()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshPortfolio, refreshSnapshots]);

  const handleToggleExcludeRealEstate = React.useCallback(() => {
    setExcludeRealEstate((v) => !v);
  }, []);

  const loading = portfolioLoading || snapshotsLoading;

  // Transform snapshots to chart data
  const chartData = React.useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    return snapshots
      .map((s) => {
        let value = s.netWorth || s.totalValue || 0;
        if (excludeRealEstate && s.categories?.realEstate?.value) {
          value -= s.categories.realEstate.value;
        }
        return { date: s.date, value };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [snapshots, excludeRealEstate]);

  // Calculate performance metrics
  const performance = React.useMemo(() => {
    if (chartData.length < 2) {
      return { change: 0, changePercent: 0 };
    }
    const startValue = chartData[0].value;
    const endValue = chartData[chartData.length - 1].value;
    const change = endValue - startValue;
    const changePercent = startValue > 0 ? (change / startValue) * 100 : 0;
    return { change, changePercent };
  }, [chartData]);

  // Asset allocation data for donut
  const allocationData = React.useMemo(() => {
    if (!summary?.breakdownEUR) return [];
    const breakdown = summary.breakdownEUR;
    const data = [];

    if (breakdown.stocks > 0) data.push({ name: "Stocks & ETFs", value: breakdown.stocks });
    if (breakdown.crypto > 0) data.push({ name: "Crypto", value: breakdown.crypto });
    if (breakdown.cash > 0) data.push({ name: "Cash", value: breakdown.cash });
    if (breakdown.realEstate > 0 && !excludeRealEstate) data.push({ name: "Real Estate", value: breakdown.realEstate });
    if (breakdown.preciousMetals > 0) data.push({ name: "Precious Metals", value: breakdown.preciousMetals });
    if ((breakdown.other || 0) > 0) data.push({ name: "Other", value: breakdown.other });

    const total = data.reduce((sum, d) => sum + d.value, 0);
    return data.map((d) => ({ ...d, percentage: total > 0 ? (d.value / total) * 100 : 0 }));
  }, [summary, excludeRealEstate]);

  // Calculate total gain/loss
  const totalGain = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return 0;
    return holdings
      .filter((h) => !h.isLiability && (!excludeRealEstate || !REAL_ESTATE_TYPES_PERF.includes(h.assetType)))
      .reduce((sum, h) => sum + (h.gainEUR || 0), 0);
  }, [holdings, excludeRealEstate]);

  const totalCost = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return 0;
    return holdings
      .filter((h) => !h.isLiability && (!excludeRealEstate || !REAL_ESTATE_TYPES_PERF.includes(h.assetType)))
      .reduce((sum, h) => {
        const units = parseFloat(h.units) || 0;
        const price = parseFloat(h.purchasePrice) || 0;
        return sum + units * price;
      }, 0);
  }, [holdings, excludeRealEstate]);

  const totalGainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

  const displayNetWorth = React.useMemo(() => {
    if (!summary?.netWorthEUR) return 0;
    if (!excludeRealEstate) return summary.netWorthEUR;
    return summary.netWorthEUR - (summary.breakdownEUR?.realEstate || 0);
  }, [summary, excludeRealEstate]);

  const displayTotalAssets = React.useMemo(() => {
    if (!summary?.totalAssetsEUR) return 0;
    if (!excludeRealEstate) return summary.totalAssetsEUR;
    return summary.totalAssetsEUR - (summary.breakdownEUR?.realEstate || 0);
  }, [summary, excludeRealEstate]);

  // Find best and worst performer + a ranked list for Top movers
  const { bestPerformer, worstPerformer, rankedPerformers } = React.useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return { bestPerformer: null, worstPerformer: null, rankedPerformers: [] };
    }

    const investmentHoldings = holdings.filter(
      (h) =>
        !h.isLiability &&
        h.gainPercent !== undefined &&
        !PERFORMANCE_EXCLUDED_TYPES.includes(h.assetType) &&
        (!excludeRealEstate || !REAL_ESTATE_TYPES_PERF.includes(h.assetType))
    );

    if (investmentHoldings.length === 0) {
      return { bestPerformer: null, worstPerformer: null, rankedPerformers: [] };
    }

    const sorted = [...investmentHoldings].sort((a, b) => (b.gainPercent || 0) - (a.gainPercent || 0));

    return {
      bestPerformer: sorted[0],
      worstPerformer: sorted[sorted.length - 1],
      rankedPerformers: sorted,
    };
  }, [holdings, excludeRealEstate]);

  // Top 3 gainers + bottom 2 losers, for the diverging-bar Top Movers panel
  // (mirrors Analytics.dc.html's 5-row mixed-sign list).
  const topMoversSlice = React.useMemo(() => {
    if (rankedPerformers.length === 0) return { top: [], bottom: [] };
    return {
      top: rankedPerformers.slice(0, 3),
      bottom: rankedPerformers.slice(-2),
    };
  }, [rankedPerformers]);

  return (
    <div className="flex flex-col gap-3.5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Net worth"
          value={loading ? "--" : new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(displayNetWorth)}
        />
        <StatCard
          label={`${rangeChangeLabel} change`}
          value={loading ? "--" : formatSignedCurrency(performance.change)}
          valueTone={performance.change >= 0 ? "positive" : "negative"}
          delta={loading ? undefined : `${performance.changePercent >= 0 ? "+" : ""}${performance.changePercent.toFixed(2)}%`}
          deltaTone={performance.changePercent >= 0 ? "positive" : "negative"}
        />
        <StatCard
          label="Total gain"
          value={loading ? "--" : formatSignedCurrency(totalGain)}
          valueTone={totalGain >= 0 ? "positive" : "negative"}
          delta={loading ? undefined : `${totalGainPercent >= 0 ? "+" : ""}${totalGainPercent.toFixed(2)}%`}
          deltaTone={totalGainPercent >= 0 ? "positive" : "negative"}
        />
        <StatCard
          label="Total assets"
          value={loading ? "--" : new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(displayTotalAssets)}
        />
      </div>

      {/* Value chart + allocation donut */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="px-[18px] py-4">
            <div className="flex items-center justify-between">
              <span className="text-[13.5px] font-semibold text-foreground">Portfolio value — {RANGE_PERIOD_NOUNS[range]}</span>
              <SegmentedControl value={range} onValueChange={setRange} aria-label="Chart time range">
                <SegmentedControlOption value="1M">1M</SegmentedControlOption>
                <SegmentedControlOption value="3M">3M</SegmentedControlOption>
                <SegmentedControlOption value="1Y">1Y</SegmentedControlOption>
                <SegmentedControlOption value="All">All</SegmentedControlOption>
              </SegmentedControl>
            </div>
            <div className="mt-3.5">
              <PortfolioValueChart data={chartData} loading={snapshotsLoading} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col px-[18px] py-4">
            <span className="text-[13.5px] font-semibold text-foreground">Asset allocation</span>
            <AllocationDonut data={allocationData} loading={portfolioLoading} />

            <div className="mt-auto grid grid-cols-2 gap-2.5 pt-3.5">
              <div className="rounded-md border border-hairline px-3 py-2.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-faint">
                  Best performer
                </div>
                {loading ? (
                  <Skeleton className="mt-1 h-4 w-20" />
                ) : bestPerformer ? (
                  <div className="mt-1 text-[12.5px] font-medium text-foreground">
                    {bestPerformer.name || bestPerformer.assetName}{" "}
                    <span className="font-mono text-[11.5px] text-success">
                      {(bestPerformer.gainPercent || 0) >= 0 ? "+" : ""}
                      {(bestPerformer.gainPercent || 0).toFixed(1)}%
                    </span>
                  </div>
                ) : (
                  <Text variant="secondary" size="xs" className="mt-1">
                    No data
                  </Text>
                )}
              </div>
              <div className="rounded-md border border-hairline px-3 py-2.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-faint">
                  Worst performer
                </div>
                {loading ? (
                  <Skeleton className="mt-1 h-4 w-20" />
                ) : worstPerformer ? (
                  <div className="mt-1 text-[12.5px] font-medium text-foreground">
                    {worstPerformer.name || worstPerformer.assetName}{" "}
                    <span
                      className={cn(
                        "font-mono text-[11.5px]",
                        (worstPerformer.gainPercent || 0) >= 0 ? "text-success" : "text-destructive"
                      )}
                    >
                      {(worstPerformer.gainPercent || 0) >= 0 ? "+" : "−"}
                      {Math.abs(worstPerformer.gainPercent || 0).toFixed(1)}%
                    </span>
                  </div>
                ) : (
                  <Text variant="secondary" size="xs" className="mt-1">
                    No data
                  </Text>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top movers + Time in market */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TopMoversPanel topPerformers={topMoversSlice.top} bottomPerformers={topMoversSlice.bottom} loading={loading} />
        <TimeInMarketPanel holdings={holdings} loading={portfolioLoading} />
      </div>

      {/* Registers this tab's Exclude-real-estate/Refresh handlers with the
          AppLayout toolbar (rendered by the parent Analytics() component) —
          see ExcludeRealEstateAndRefreshBridge/AnalyticsToolbarContext
          below. Renders nothing itself. */}
      <ExcludeRealEstateAndRefreshBridge
        excludeRealEstate={excludeRealEstate}
        onToggleExcludeRealEstate={handleToggleExcludeRealEstate}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
    </div>
  );
}

/**
 * ExcludeRealEstateAndRefreshBridge — registers this tab's toggle/refresh
 * handlers with the shared AnalyticsToolbarContext so AppLayout's toolbar
 * (rendered by the parent Analytics() component, above this tab's own tree)
 * can drive them. See Analytics() for the provider.
 */
function ExcludeRealEstateAndRefreshBridge({ excludeRealEstate, onToggleExcludeRealEstate, onRefresh, isRefreshing }) {
  const registerToolbarActions = React.useContext(AnalyticsToolbarContext);
  React.useEffect(() => {
    registerToolbarActions?.({ excludeRealEstate, onToggleExcludeRealEstate, onRefresh, isRefreshing });
  }, [registerToolbarActions, excludeRealEstate, onToggleExcludeRealEstate, onRefresh, isRefreshing]);
  return null;
}

const AnalyticsToolbarContext = React.createContext(null);

// Insights Page Component (§5 "Analytics/Insights" — retheme only, see
// module JSDoc for the TopPerformers/ConcentrationRisk/ETFComparison/
// PerformanceHeatmap scoping decision).
function InsightsPage() {
  const [excludeRealEstate, setExcludeRealEstate] = React.useState(false);

  const {
    holdings,
    filterOptions,
    loading: portfolioLoading,
    refresh: refreshPortfolio,
  } = usePortfolio();

  const { data: analyticsData, loading: analyticsLoading, refresh: refreshAnalytics } = useAnalyticsSummary({ excludeRealEstate });
  const { data: etfData, loading: etfLoading, refresh: refreshETF } = useETFComparison(90);
  const { data: heatmapData, loading: heatmapLoading, refresh: refreshHeatmap } = usePerformanceHeatmap(365);

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refreshPortfolio(), refreshAnalytics(), refreshETF(), refreshHeatmap()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshPortfolio, refreshAnalytics, refreshETF, refreshHeatmap]);

  const registerToolbarActions = React.useContext(AnalyticsToolbarContext);
  React.useEffect(() => {
    registerToolbarActions?.({
      excludeRealEstate,
      onToggleExcludeRealEstate: () => setExcludeRealEstate((v) => !v),
      onRefresh: handleRefresh,
      isRefreshing,
    });
  }, [registerToolbarActions, excludeRealEstate, handleRefresh, isRefreshing]);

  const topPerformers = analyticsData?.topPerformers || [];
  const bottomPerformers = analyticsData?.bottomPerformers || [];
  const concentrationRisk = analyticsData?.concentrationRisk;
  const averageGain = analyticsData?.averageGain;

  const REAL_ESTATE_TYPES = ["Real Estate", "Real Estate & Property"];

  const currencyAllocation = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return [];
    const currencyMap = {};
    holdings
      .filter((h) => !h.isLiability && (!excludeRealEstate || !REAL_ESTATE_TYPES.includes(h.assetType)))
      .forEach((h) => {
        const currency = h.currency || "EUR";
        currencyMap[currency] = (currencyMap[currency] || 0) + (h.totalValueEUR || 0);
      });
    const total = Object.values(currencyMap).reduce((sum, v) => sum + v, 0);
    return Object.entries(currencyMap)
      .map(([name, value], index) => ({
        name,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0,
        color: INSIGHTS_ALLOCATION_COLORS[index % INSIGHTS_ALLOCATION_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, excludeRealEstate]);

  const platformAllocation = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return [];
    const platformMap = {};
    holdings
      .filter((h) => !h.isLiability && (!excludeRealEstate || !REAL_ESTATE_TYPES.includes(h.assetType)))
      .forEach((h) => {
        const platform = h.platform || "Other";
        platformMap[platform] = (platformMap[platform] || 0) + (h.totalValueEUR || 0);
      });
    const total = Object.values(platformMap).reduce((sum, v) => sum + v, 0);
    return Object.entries(platformMap)
      .map(([name, value], index) => ({
        name,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0,
        color: INSIGHTS_ALLOCATION_COLORS[index % INSIGHTS_ALLOCATION_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, excludeRealEstate]);

  const renderAllocationCard = (data, title, Icon) => (
    <Card>
      <CardHeader className="border-b border-hairline px-[18px] py-3">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-accent-text" aria-hidden="true" />
          <CardTitle className="text-[13.5px] font-semibold">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-[18px] py-3.5">
        {portfolioLoading ? (
          <div className="flex h-[100px] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
          </div>
        ) : data.length === 0 ? (
          <Text variant="secondary" size="sm" className="py-4 text-center">
            No data
          </Text>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-20 shrink-0">
              <ResponsiveContainer width="100%" height={80}>
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={30} innerRadius={20}>
                    {data.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-0.5">
              {data.slice(0, 5).map((item) => (
                <div key={item.name} className="flex items-center gap-2 text-xs">
                  <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} aria-hidden="true" />
                  <span className="flex-1 truncate text-text-secondary">{item.name}</span>
                  <PercentValue value={item.percentage} size="xs" showSign={false} colorize={false} decimals={0} />
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const loading = portfolioLoading || analyticsLoading;

  return (
    <div className="flex flex-col gap-3.5">
      {/* Average Gain Summary Card */}
      {averageGain && (
        <Card>
          <CardContent className="flex items-center justify-between px-[18px] py-4">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "rounded-full p-2",
                  averageGain.averageGainPercent >= 0 ? "bg-success/10" : "bg-destructive/10"
                )}
              >
                {averageGain.averageGainPercent >= 0 ? (
                  <TrendingUp className="h-5 w-5 text-success" aria-hidden="true" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-destructive" aria-hidden="true" />
                )}
              </div>
              <div>
                <Label className="text-xs">Overall Portfolio Gain</Label>
                <div className="mt-0.5 flex items-center gap-2">
                  <PercentValue value={averageGain.averageGainPercent} size="lg" showTrend className="font-bold" />
                  <CurrencyValue
                    value={averageGain.totalGainEUR}
                    size="sm"
                    colorize
                    showSign
                    className="text-muted-foreground"
                  />
                </div>
              </div>
            </div>
            <Text variant="secondary" size="xs">
              Across {averageGain.holdingCount} investments
            </Text>
          </CardContent>
        </Card>
      )}

      <TopPerformers topPerformers={topPerformers} bottomPerformers={bottomPerformers} loading={loading} />

      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
        <ConcentrationRisk data={concentrationRisk} loading={analyticsLoading} />
        <ETFComparison data={etfData} loading={etfLoading} />
      </div>

      <PerformanceHeatmap data={heatmapData} loading={heatmapLoading} />

      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
        {renderAllocationCard(currencyAllocation, "By Currency", Coins)}
        {renderAllocationCard(platformAllocation, "By Platform", Building2)}
      </div>

      <Card>
        <CardHeader className="border-b border-hairline px-[18px] py-3">
          <CardTitle className="text-[13.5px] font-semibold">
            Portfolio Summary
            {excludeRealEstate && (
              <span className="ml-2 text-xs font-normal text-text-faint">(excluding real estate)</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-[18px] py-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <Label className="text-xs">Total Holdings</Label>
              <MonoText size="lg" weight="semibold" className="mt-0.5 block">
                {portfolioLoading
                  ? "--"
                  : excludeRealEstate
                  ? holdings?.filter((h) => !REAL_ESTATE_TYPES.includes(h.assetType))?.length || 0
                  : holdings?.length || 0}
              </MonoText>
            </div>
            <div>
              <Label className="text-xs">Asset Types</Label>
              <MonoText size="lg" weight="semibold" className="mt-0.5 block">
                {portfolioLoading
                  ? "--"
                  : excludeRealEstate
                  ? filterOptions?.assetTypes?.filter((t) => !REAL_ESTATE_TYPES.includes(t))?.length || 0
                  : filterOptions?.assetTypes?.length || 0}
              </MonoText>
            </div>
            <div>
              <Label className="text-xs">Currencies</Label>
              <MonoText size="lg" weight="semibold" className="mt-0.5 block">
                {portfolioLoading ? "--" : filterOptions?.currencies?.length || 0}
              </MonoText>
            </div>
            <div>
              <Label className="text-xs">Platforms</Label>
              <MonoText size="lg" weight="semibold" className="mt-0.5 block">
                {portfolioLoading ? "--" : filterOptions?.platforms?.length || 0}
              </MonoText>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Analytics() {
  const { type } = useParams();

  const pageTitles = {
    performance: "Performance",
    insights: "Insights",
    "what-if": "What If",
  };
  const pageTitle = pageTitles[type] || "Insights";

  const [toolbarActions, setToolbarActions] = React.useState(null);
  const registerToolbarActions = React.useCallback((actions) => setToolbarActions(actions), []);

  const showToolbarExtras = type === "performance" || type === "insights" || !type;

  const secondaryActions = showToolbarExtras && toolbarActions ? (
    <>
      <label className="inline-flex cursor-pointer items-center gap-[7px] text-xs text-text-secondary">
        <span className="relative inline-block h-[15px] w-[26px] shrink-0">
          <input
            type="checkbox"
            checked={toolbarActions.excludeRealEstate}
            onChange={toolbarActions.onToggleExcludeRealEstate}
            className="peer absolute h-0 w-0 opacity-0"
            aria-label="Exclude real estate"
          />
          <span
            className={cn(
              "absolute inset-0 rounded-lg transition-colors duration-[120ms] ease-out",
              toolbarActions.excludeRealEstate ? "bg-[var(--accent,_#3b9eff)]" : "bg-border"
            )}
          />
          <span
            className={cn(
              // §6 "Transitions: 120ms ease color/background/border only. No
              // transform/scale animations" — round-2 fix, ui-design-expert
              // review finding F2: the thumb's position is driven by `left`
              // (not `translate`), so a `transition-transform` class was
              // animating nothing visible, but it still contradicted §6's
              // letter. Only the thumb's background-color is now transitioned
              // (`transition-colors`); the `left` position snaps instantly.
              "absolute top-[2px] h-[11px] w-[11px] rounded-full bg-[var(--on-accent,_#06121f)] transition-colors duration-[120ms] ease-out",
              toolbarActions.excludeRealEstate ? "left-[13px] bg-white" : "left-[2px] bg-text-faint"
            )}
          />
        </span>
        Exclude real estate
      </label>
      <Button variant="outline" size="sm" onClick={toolbarActions.onRefresh} disabled={toolbarActions.isRefreshing}>
        <RefreshCw className={cn("h-3.5 w-3.5", toolbarActions.isRefreshing && "animate-spin")} aria-hidden="true" />
        Refresh
      </Button>
    </>
  ) : null;

  const renderPage = () => {
    switch (type) {
      case "performance":
        return <PerformancePage />;
      case "what-if":
        return <WhatIfSimulator />;
      case "insights":
      default:
        return <InsightsPage />;
    }
  };

  return (
    <AnalyticsToolbarContext.Provider value={registerToolbarActions}>
      <AppLayout title={pageTitle} secondaryActions={secondaryActions}>
        <div className="mx-auto max-w-[1600px]">{renderPage()}</div>
      </AppLayout>
    </AnalyticsToolbarContext.Provider>
  );
}

export default Analytics;
